import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { playerProfileService } from '../../services/playerMasterService';
import { format } from 'date-fns';

const AdminAuditDashboard = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const response = await playerProfileService?.getAllForAdmin();
      setPlayers(response?.data || []);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate data completeness percentage
  const calculateCompleteness = (player) => {
    const fields = [
      player?.fullName,
      player?.dateOfBirth,
      player?.placeOfBirth,
      player?.nationalityPrimary,
      player?.height,
      player?.weight,
      player?.preferredFoot,
      player?.primaryPosition,
      player?.currentClub,
      player?.currentLeague,
      player?.currentCountry,
      player?.contractType,
      player?.contractStartDate,
      player?.contractEndDate,
      player?.currentMarketValue,
      player?.agentName,
      player?.agencyName,
      player?.agentEmail,
      player?.agentPhone,
      player?.profilePhoto
    ];

    const filledFields = fields?.filter(field => field !== null && field !== undefined && field !== '')?.length;
    const totalFields = fields?.length;
    return Math.round((filledFields / totalFields) * 100);
  };

  // Determine sync status based on data source and last update
  const getSyncStatus = (player) => {
    if (!player?.dataSource) return { status: 'manual', label: 'Manual Entry', color: 'bg-gray-100 text-gray-800' };
    
    if (player?.dataSource === 'transfermarkt') {
      const lastUpdate = new Date(player?.lastUpdated || player?.updatedAt);
      const now = new Date();
      const daysSinceUpdate = Math.floor((now - lastUpdate) / (1000 * 60 * 60 * 24));
      
      if (daysSinceUpdate > 30) {
        return { status: 'outdated', label: 'Outdated', color: 'bg-red-100 text-red-800' };
      } else if (daysSinceUpdate > 7) {
        return { status: 'stale', label: 'Needs Update', color: 'bg-yellow-100 text-yellow-800' };
      } else {
        return { status: 'synced', label: 'Synced', color: 'bg-green-100 text-green-800' };
      }
    }
    
    return { status: 'manual', label: 'Manual Entry', color: 'bg-blue-100 text-blue-800' };
  };

  // Get saved fields count
  const getSavedFieldsCount = (player) => {
    const allFields = [
      'fullName', 'knownAs', 'dateOfBirth', 'placeOfBirth',
      'nationalityPrimary', 'nationalitySecondary',
      'height', 'weight', 'preferredFoot', 'primaryPosition', 'secondaryPositions',
      'currentClub', 'currentLeague', 'currentCountry', 'squadNumber',
      'contractType', 'contractStartDate', 'contractEndDate',
      'availableForTransfer', 'availableForLoan',
      'currentMarketValue', 'highestMarketValue', 'currency',
      'agentName', 'agencyName', 'agentEmail', 'agentPhone', 'representationContractUntil',
      'profileVisibility', 'verifiedStatus', 'fifaVerified',
      'profilePhoto', 'actionPhotos', 'dataSource'
    ];

    const savedFields = allFields?.filter(field => {
      const value = player?.[field];
      return value !== null && value !== undefined && value !== '';
    });

    return { saved: savedFields?.length, total: allFields?.length };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  // Sort players
  const sortedPlayers = [...players]?.sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'createdAt':
        aValue = new Date(a?.createdAt);
        bValue = new Date(b?.createdAt);
        break;
      case 'updatedAt':
        aValue = new Date(a?.updatedAt || a?.lastUpdated);
        bValue = new Date(b?.updatedAt || b?.lastUpdated);
        break;
      case 'completeness':
        aValue = calculateCompleteness(a);
        bValue = calculateCompleteness(b);
        break;
      case 'name':
        aValue = a?.fullName?.toLowerCase() || '';
        bValue = b?.fullName?.toLowerCase() || '';
        break;
      default:
        return 0;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Filter players
  const filteredPlayers = sortedPlayers?.filter(player => {
    const searchLower = searchTerm?.toLowerCase();
    return (
      player?.fullName?.toLowerCase()?.includes(searchLower) ||
      player?.currentClub?.toLowerCase()?.includes(searchLower) ||
      player?.nationalityPrimary?.toLowerCase()?.includes(searchLower)
    );
  });

  // Stats calculations
  const totalPlayers = players?.length || 0;
  const avgCompleteness = players?.length > 0
    ? Math.round(players?.reduce((sum, p) => sum + calculateCompleteness(p), 0) / players?.length)
    : 0;
  const syncedCount = players?.filter(p => getSyncStatus(p)?.status === 'synced')?.length || 0;
  const manualCount = players?.filter(p => getSyncStatus(p)?.status === 'manual')?.length || 0;

  return (
    <>
      <Helmet>
        <title>Admin Audit Dashboard - ProX11Market</title>
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          {/* Header */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 font-headline">
                Admin Audit Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manual verification of player data completeness and sync status
              </p>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={fetchPlayers}
              loading={loading}
            >
              Refresh Data
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Players</span>
                <Icon name="Users" size={20} color="var(--color-primary)" />
              </div>
              <p className="text-3xl font-bold text-foreground">{totalPlayers}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Avg Completeness</span>
                <Icon name="PieChart" size={20} color="var(--color-success)" />
              </div>
              <p className="text-3xl font-bold text-foreground">{avgCompleteness}%</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Synced Players</span>
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              </div>
              <p className="text-3xl font-bold text-foreground">{syncedCount}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Manual Entries</span>
                <Icon name="Edit" size={20} color="var(--color-warning)" />
              </div>
              <p className="text-3xl font-bold text-foreground">{manualCount}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Search Players
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="text"
                    placeholder="Search by name, club, nationality..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="createdAt">Creation Date</option>
                  <option value="updatedAt">Last Update</option>
                  <option value="completeness">Completeness</option>
                  <option value="name">Name</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Sort Order
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Audit Table */}
          {loading ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Loader" size={48} className="mx-auto mb-4 text-muted-foreground animate-spin" />
              <p className="text-muted-foreground">Loading player data...</p>
            </div>
          ) : filteredPlayers?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Players Found</h3>
              <p className="text-muted-foreground">Try adjusting your search criteria</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Player
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Saved Fields
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Completeness
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Sync Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredPlayers?.map((player) => {
                      const completeness = calculateCompleteness(player);
                      const syncStatus = getSyncStatus(player);
                      const fieldCount = getSavedFieldsCount(player);

                      return (
                        <tr key={player?.id} className="hover:bg-muted/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                <Icon name="User" size={20} color="var(--color-muted-foreground)" />
                              </div>
                              <div>
                                <div className="font-medium text-foreground">{player?.fullName || 'N/A'}</div>
                                <div className="text-sm text-muted-foreground">
                                  {player?.currentClub || 'Free Agent'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">{formatDate(player?.createdAt)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">
                              {formatDate(player?.updatedAt || player?.lastUpdated)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground font-medium">
                              {fieldCount?.saved} / {fieldCount?.total}
                            </div>
                            <div className="text-xs text-muted-foreground">fields saved</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-muted rounded-full h-2 w-24">
                                <div
                                  className={`h-2 rounded-full ${
                                    completeness >= 80
                                      ? 'bg-success'
                                      : completeness >= 50
                                      ? 'bg-warning' :'bg-error'
                                  }`}
                                  style={{ width: `${completeness}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-foreground">{completeness}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${syncStatus?.color}`}
                            >
                              {syncStatus?.status === 'synced' && <Icon name="CheckCircle" size={12} />}
                              {syncStatus?.status === 'outdated' && <Icon name="AlertCircle" size={12} />}
                              {syncStatus?.status === 'stale' && <Icon name="Clock" size={12} />}
                              {syncStatus?.status === 'manual' && <Icon name="Edit" size={12} />}
                              {syncStatus?.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminAuditDashboard;